/** @fileoverview Shows the files sent with a message: images as a stack with a viewer, documents by name. */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { AttachmentData } from '../../types/chat';
import DeletedAttachment from './DeletedAttachment';
import DocumentAttachments from './DocumentAttachments';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

const STACK_LAYERS = ['z-30', 'z-20 rotate-6 translate-x-1', 'z-10 -rotate-6 -translate-x-1'];

/** Sent images as one small stack above the message, click it to page through them larger; documents are listed by name. */
export default function MessageAttachments({ attachments }: { attachments: AttachmentData[] }) {
  const viewable = attachments.filter(
    (item) => item.type !== 'document' && !item.deleted && item.url
  );
  const missingImages = attachments.filter(
    (item) => item.type !== 'document' && (item.deleted || !item.url)
  );
  const documents = attachments.filter((item) => item.type === 'document');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const current = openIndex === null ? undefined : viewable[openIndex];
  const move = (step: number) =>
    setOpenIndex((index) =>
      index === null ? index : (index + step + viewable.length) % viewable.length
    );

  return (
    <>
      {documents.length > 0 && <DocumentAttachments documents={documents} />}
      {missingImages.length > 0 && (
        <ul
          className="ml-auto flex max-w-full flex-col items-end gap-1.5"
          aria-label="Unavailable images"
        >
          {missingImages.map((image) => (
            <DeletedAttachment
              key={image.id}
              name={image.name ?? 'Image'}
              status={image.deleted ? 'deleted' : 'unavailable'}
            />
          ))}
        </ul>
      )}
      {viewable.length > 0 && (
        <div className="ml-auto flex items-center gap-3">
          <button
            aria-label={viewable.length > 1 ? `View ${viewable.length} images` : 'View image'}
            className="relative h-20 w-20 cursor-pointer rounded-lg"
            onClick={() => setOpenIndex(0)}
            type="button"
          >
            {STACK_LAYERS.slice(0, Math.min(viewable.length, 3)).map((layer, index) => (
              <img
                alt=""
                decoding="async"
                loading="lazy"
                className={`absolute inset-0 h-full w-full rounded-lg border border-app-border object-cover shadow-sm ${layer}`}
                key={viewable[index].id}
                src={viewable[index].url}
              />
            ))}
            {viewable.length > 1 && (
              <span className="absolute -top-2 -right-2 z-40 rounded-full border border-app-border-strong bg-app-surface-card px-1.5 text-xs font-medium text-app-text">
                {viewable.length}
              </span>
            )}
          </button>
          <Dialog open={current !== undefined} onOpenChange={(open) => !open && setOpenIndex(null)}>
            <DialogContent
              className="flex w-fit max-w-screen items-center justify-center overflow-hidden border-0 p-0 sm:max-w-screen"
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') move(-1);
                if (event.key === 'ArrowRight') move(1);
              }}
            >
              <DialogTitle className="sr-only">Image</DialogTitle>
              {current && (
                <img
                  alt="Attached"
                  className="max-h-screen max-w-screen rounded-md object-contain"
                  src={current.url}
                />
              )}
              {viewable.length > 1 && (
                <>
                  <button
                    aria-label="Previous image"
                    className="absolute top-1/2 left-3 -translate-y-1/2 rounded-full border border-app-border-strong bg-app-surface-card p-2 text-app-text"
                    onClick={() => move(-1)}
                    type="button"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    aria-label="Next image"
                    className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full border border-app-border-strong bg-app-surface-card p-2 text-app-text"
                    onClick={() => move(1)}
                    type="button"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-app-border-strong bg-app-surface-card px-2 text-xs text-app-text">
                    {(openIndex ?? 0) + 1} / {viewable.length}
                  </span>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </>
  );
}
